var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/**
 * @file img crop
 * @author wangpantuo
 */
import React, { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import LocaleReceiver from '../locale-provider/LocaleReceiver';
import Modal from '../modal';
import Upload from '../upload';
import EasyCrop from './EasyCrop';
import { ConfigContext } from '../config-provider';
import { INIT_ROTATE, INIT_ZOOM } from './constants';
const ImgCrop = forwardRef((props, cropperRef) => {
    const { aspect = 1, shape = 'rect', grid = false, quality = 0.4, fillColor = 'white', prefixCls: customPrefixCls, zoom = true, rotate = false, minZoom = 1, maxZoom = 3, modalTitle, modalWidth, modalOk, modalCancel, modalMaskTransitionName, modalTransitionName, modalClassName, onModalOk, onModalCancel, beforeCrop, onUploadFail, cropperProps, children, } = props;
    const { getPrefixCls } = React.useContext(ConfigContext);
    const prefixCls = getPrefixCls('imgcrop', customPrefixCls);
    const cb = useRef({});
    cb.current.onModalOk = onModalOk;
    cb.current.onModalCancel = onModalCancel;
    cb.current.beforeCrop = beforeCrop;
    cb.current.onUploadFail = onUploadFail;
    /**
     * Upload
     */
    const [image, setImage] = useState('');
    const fileRef = useRef({});
    const beforeUploadRef = useRef();
    const resolveRef = useRef(() => { });
    const rejectRef = useRef(() => { });
    const uploadComponent = useMemo(() => {
        const upload = Array.isArray(children) ? children[0] : children;
        const _a = upload.props, { beforeUpload, accept } = _a, restUploadProps = __rest(_a, ["beforeUpload", "accept"]);
        beforeUploadRef.current = beforeUpload;
        return Object.assign(Object.assign({}, upload), { props: Object.assign(Object.assign({}, restUploadProps), { accept: accept || 'image/*', beforeUpload: (file, fileList) => {
                    return new Promise((resolve, reject) => __awaiter(void 0, void 0, void 0, function* () {
                        if (cb.current.beforeCrop) {
                            const shouldCrop = yield cb.current.beforeCrop(file, fileList);
                            if (!shouldCrop) {
                                return reject();
                            }
                        }
                        fileRef.current = file;
                        resolveRef.current = newFile => {
                            var _a, _b;
                            (_b = (_a = cb.current).onModalOk) === null || _b === void 0 ? void 0 : _b.call(_a, newFile);
                            resolve(newFile);
                        };
                        rejectRef.current = uploadErr => {
                            var _a, _b;
                            (_b = (_a = cb.current).onUploadFail) === null || _b === void 0 ? void 0 : _b.call(_a, uploadErr);
                            reject();
                        };
                        const reader = new FileReader();
                        reader.addEventListener('load', () => {
                            if (typeof reader.result === 'string') {
                                setImage(reader.result);
                            }
                        });
                        reader.readAsDataURL(file);
                    }));
                } }) });
    }, [children]);
    /**
     * Crop
     */
    const easyCropRef = useRef({});
    /**
     * Modal
     */
    const modalProps = useMemo(() => {
        const obj = {
            width: modalWidth,
            okText: modalOk,
            cancelText: modalCancel,
            maskTransitionName: modalMaskTransitionName,
            transitionName: modalTransitionName,
        };
        Object.keys(obj).forEach(prop => {
            const key = prop;
            if (obj[key] === undefined) {
                delete obj[key];
            }
        });
        return obj;
    }, [
        modalCancel,
        modalMaskTransitionName,
        modalOk,
        modalTransitionName,
        modalWidth,
    ]);
    const onClose = () => {
        setImage('');
        easyCropRef.current.setZoomVal(INIT_ZOOM);
        easyCropRef.current.setRotateVal(INIT_ROTATE);
    };
    const onCancel = useCallback(() => {
        var _a, _b;
        (_b = (_a = cb.current).onModalCancel) === null || _b === void 0 ? void 0 : _b.call(_a);
        onClose();
    }, []);
    const onOk = useCallback((event) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        onClose();
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const target = event.target;
        const context = ((_a = target === null || target === void 0 ? void 0 : target.getRootNode) === null || _a === void 0 ? void 0 : _a.call(target))
            || document;
        const imgSource = context.querySelector(`.${prefixCls}-media`);
        const { width: cropWidth, height: cropHeight, x: cropX, y: cropY, } = easyCropRef.current.cropPixelsRef.current;
        if (rotate && easyCropRef.current.rotateVal !== INIT_ROTATE) {
            const { naturalWidth: imgWidth, naturalHeight: imgHeight } = imgSource;
            const angle = easyCropRef.current.rotateVal * (Math.PI / 180);
            // get container for rotated image
            const sine = Math.abs(Math.sin(angle));
            const cosine = Math.abs(Math.cos(angle));
            const squareWidth = imgWidth * cosine + imgHeight * sine;
            const squareHeight = imgHeight * cosine + imgWidth * sine;
            canvas.width = squareWidth;
            canvas.height = squareHeight;
            ctx.fillStyle = fillColor;
            ctx.fillRect(0, 0, squareWidth, squareHeight);
            // rotate container
            const squareHalfWidth = squareWidth / 2;
            const squareHalfHeight = squareHeight / 2;
            ctx.translate(squareHalfWidth, squareHalfHeight);
            ctx.rotate(angle);
            ctx.translate(-squareHalfWidth, -squareHalfHeight);
            // draw rotated image
            const imgX = (squareWidth - imgWidth) / 2;
            const imgY = (squareHeight - imgHeight) / 2;
            ctx.drawImage(imgSource, 0, 0, imgWidth, imgHeight, imgX, imgY, imgWidth, imgHeight);
            // crop rotated image
            const imgData = ctx.getImageData(0, 0, squareWidth, squareHeight);
            canvas.width = cropWidth;
            canvas.height = cropHeight;
            ctx.putImageData(imgData, -cropX, -cropY);
        }
        else {
            canvas.width = cropWidth;
            canvas.height = cropHeight;
            ctx.fillStyle = fillColor;
            ctx.fillRect(0, 0, cropWidth, cropHeight);
            ctx.drawImage(imgSource, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
        }
        // get the new image
        const { type, name, uid } = fileRef.current;
        canvas.toBlob((blob) => __awaiter(void 0, void 0, void 0, function* () {
            const newFile = Object.assign(new File([blob], name, { type }), { uid });
            if (!beforeUploadRef.current) {
                return resolveRef.current(newFile);
            }
            const rcFile = newFile;
            const result = yield beforeUploadRef.current(rcFile, [
                rcFile,
            ]);
            if (result === true) {
                return resolveRef.current(newFile);
            }
            if (result === false) {
                return rejectRef.current(new Error('beforeUpload return false'));
            }
            delete newFile[Upload.LIST_IGNORE];
            if (result === Upload.LIST_IGNORE) {
                Object.defineProperty(newFile, Upload.LIST_IGNORE, {
                    value: true,
                    configurable: true,
                });
                return rejectRef.current(new Error('beforeUpload return LIST_IGNORE'));
            }
            if (typeof result === 'object' && result !== null) {
                return resolveRef.current(result);
            }
        }), type, quality);
    }), [fillColor, quality, rotate]);
    const getComponent = (titleOfModal) => (<>
            {uploadComponent}
            {image && (<Modal visible className={`${prefixCls}-modal ${modalClassName || ''}`} title={titleOfModal} onOk={onOk} onCancel={onCancel} maskClosable={false} destroyOnClose {...modalProps}>
                    <EasyCrop ref={easyCropRef} cropperRef={cropperRef} image={image} aspect={aspect} shape={shape} grid={grid} zoom={zoom} rotate={rotate} minZoom={minZoom} maxZoom={maxZoom} cropperProps={cropperProps}/>
                </Modal>)}
        </>);
    if (modalTitle) {
        return getComponent(modalTitle);
    }
    return (<LocaleReceiver componentName="ImgCrop">
            {(locale) => {
            return getComponent(locale.modalTitle);
        }}
        </LocaleReceiver>);
});
export default ImgCrop;
